(() => {
  if (customElements.get('maxton-chat-bot')) return;

  // Constants
  const TRANSCRIPT_STORAGE_KEY = 'maxton:chat-bot:transcript:v1';
  const RESUME_STORAGE_KEY = 'maxton:chat-bot:resume:v1';
  const RESUME_TTL_MS = 60 * 60 * 1000;
  const RESUMABLE_VIEW_KINDS = new Set(['vehicle-level', 'vehicle-picker']);
  const TRANSCRIPT_LIMIT = 30;
  const SEARCH_RESULT_LIMIT = 20;
  const PRODUCT_CARD_LIMIT = 3;
  const CATALOG_PAGE_LIMIT = 250;
  const CATALOG_MAX_PAGES = 8;
  const PRODUCT_IMAGE_WIDTH = 160;
  const PRODUCT_THUMB_SIZE = 56;
  const PATH_SEPARATOR = ' › ';

  // Generic helpers
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const normalizeText = (value = '') =>
    String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const interpolateText = (template = '', variables = {}) =>
    template.replace(/\{(\w+)\}/g, (placeholder, key) => variables[key] ?? placeholder);

  const createElement = (tagName, { className, text, attributes = {} } = {}, children = []) => {
    const element = document.createElement(tagName);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== undefined && value !== null && value !== false) element.setAttribute(name, value === true ? '' : value);
    });
    element.append(...children.filter(Boolean));
    return element;
  };

  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      const script = createElement('script', { attributes: { src } });
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), { once: true });
      document.head.append(script);
    });

  const fetchJson = async (url) => {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`);
    return response.json();
  };

  const trackChatEvent = (event, params = {}) => {
    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ event, ...params });
  };

  // Session storage (transcript and resume point survive following a chat link)
  const readSessionJson = (key) => {
    try {
      return JSON.parse(window.sessionStorage.getItem(key));
    } catch {
      return null;
    }
  };

  const writeSessionJson = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage unavailable (private mode, quota), this state is optional
    }
  };

  const removeSessionItem = (key) => {
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // Storage unavailable, nothing to clear
    }
  };

  const readStoredTranscript = () => {
    const transcript = readSessionJson(TRANSCRIPT_STORAGE_KEY);
    return Array.isArray(transcript) ? transcript : [];
  };

  const readResumeState = () => {
    const resumeState = readSessionJson(RESUME_STORAGE_KEY);
    const isValid = Array.isArray(resumeState?.frames) && Date.now() - (resumeState.savedAt ?? 0) < RESUME_TTL_MS;
    return isValid ? resumeState : null;
  };

  // Vehicle taxonomy index
  const buildVehicleIndex = ({ rootId, nodes }) => {
    const nodeById = new Map(nodes.map((node) => [Number(node.id), node]));
    const childrenByParent = new Map();
    const pathLabelsById = new Map();
    let searchEntries = null;

    nodes.forEach((node) => {
      const parentId = Number(node.parentId);
      if (!childrenByParent.has(parentId)) childrenByParent.set(parentId, []);
      childrenByParent.get(parentId).push(node);
    });

    const getPathLabels = (nodeId) => {
      if (!pathLabelsById.has(nodeId)) {
        const labels = [];
        for (let node = nodeById.get(nodeId); node; node = nodeById.get(Number(node.parentId))) labels.unshift(node.label);
        pathLabelsById.set(nodeId, labels);
      }
      return pathLabelsById.get(nodeId);
    };

    const getSearchEntries = () => {
      searchEntries ??= nodes.map((node) => {
        const labels = getPathLabels(Number(node.id));
        return {
          nodeId: Number(node.id),
          depth: labels.length,
          label: labels.join(PATH_SEPARATOR),
          searchText: normalizeText(labels.join(' ')),
        };
      });
      return searchEntries;
    };

    return {
      rootId: Number(rootId),
      getNode: (nodeId) => nodeById.get(Number(nodeId)) ?? null,
      getChildren: (nodeId) => childrenByParent.get(Number(nodeId)) ?? [],
      getPathLabel: (nodeId) => getPathLabels(Number(nodeId)).join(PATH_SEPARATOR),
      searchNodes: (query) => {
        const tokens = normalizeText(query).split(' ').filter(Boolean);
        if (!tokens.length) return [];

        return getSearchEntries()
          .filter(({ searchText }) => tokens.every((token) => searchText.includes(token)))
          .sort((left, right) => left.depth - right.depth || left.label.localeCompare(right.label))
          .slice(0, SEARCH_RESULT_LIMIT);
      },
    };
  };

  // Collection catalog (product counts per category tag)
  const readProductTags = ({ tags }) => {
    const tagList = Array.isArray(tags) ? tags : String(tags ?? '').split(',');
    return new Set(tagList.map((tag) => normalizeText(tag)).filter(Boolean));
  };

  const readLowestVariantPriceInCents = ({ variants = [] }) => {
    const prices = variants.map(({ price }) => Math.round(Number.parseFloat(price) * 100)).filter(Number.isFinite);
    return prices.length ? Math.min(...prices) : null;
  };

  const summarizeCatalog = (products, { isComplete = true } = {}) => {
    const catalogProducts = products.map((product) => ({ ...product, tagSet: readProductTags(product) }));

    return {
      products: catalogProducts,
      total: catalogProducts.length,
      isComplete,
      countsByTag: catalogProducts.reduce((counts, { tagSet }) => {
        tagSet.forEach((tag) => counts.set(tag, (counts.get(tag) ?? 0) + 1));
        return counts;
      }, new Map()),
    };
  };

  const filterProductsByTag = (products, tag) => products.filter(({ tagSet }) => tagSet.has(normalizeText(tag)));

  const formatCatalogCount = (count, { isComplete = true } = {}) => (isComplete ? `${count}` : `${count}+`);

  const fetchAllCatalogPages = async (collectionPath) => {
    const products = [];

    for (let page = 1; page <= CATALOG_MAX_PAGES; page += 1) {
      const { products: pageProducts = [] } = await fetchJson(`${collectionPath}/products.json?limit=${CATALOG_PAGE_LIMIT}&page=${page}`);
      products.push(...pageProducts);
      if (pageProducts.length < CATALOG_PAGE_LIMIT) return summarizeCatalog(products);
    }

    return summarizeCatalog(products, { isComplete: false });
  };

  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

  const createChevronIcon = () => {
    const svg = document.createElementNS(SVG_NAMESPACE, 'svg');
    const path = document.createElementNS(SVG_NAMESPACE, 'path');
    Object.entries({ class: 'chat-bot__product-chevron', width: 18, height: 18, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round', 'stroke-linejoin': 'round', 'aria-hidden': 'true', focusable: 'false' })
      .forEach(([name, value]) => svg.setAttribute(name, value));
    path.setAttribute('d', 'M9 6l6 6-6 6');
    svg.append(path);
    return svg;
  };

  const buildResizedImageUrl = (src, width) => {
    if (!src) return null;
    const url = new URL(src, window.location.origin);
    if (!url.protocol.startsWith('http')) return src;
    url.searchParams.set('width', width);
    return url.toString();
  };

  // Chat bot element
  class MaxtonChatBot extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;

      this.config = JSON.parse(this.querySelector('[data-chat-bot-config]').textContent);
      this.locale = String(this.config.locale ?? 'pl').toLowerCase();
      this.rootPrefix = String(this.config.rootUrl ?? '/').replace(/\/$/, '');
      this.elements = {
        launcher: this.querySelector('[data-chat-bot-launcher]'),
        panel: this.querySelector('.chat-bot__panel'),
        close: this.querySelector('[data-chat-bot-close]'),
        messages: this.querySelector('[data-chat-bot-messages]'),
        controls: this.querySelector('[data-chat-bot-controls]'),
        back: this.querySelector('[data-chat-bot-back]'),
        restart: this.querySelector('[data-chat-bot-restart]'),
      };

      this.flow = null;
      this.flowPromise = null;
      this.vehicleIndex = null;
      this.frames = [];
      this.isBusy = false;
      this.previousTranscript = readStoredTranscript();

      this.bindEvents();
    }

    // Event binding
    bindEvents() {
      const { launcher, panel, close, back, restart, controls } = this.elements;

      launcher.addEventListener('click', () => (panel.hidden ? this.openPanel() : this.closePanel()));
      close.addEventListener('click', () => this.closePanel());
      back.addEventListener('click', () => this.goBack());
      restart.addEventListener('click', () => this.restartConversation());
      panel.addEventListener('keydown', (event) => event.key === 'Escape' && this.closePanel());

      controls.addEventListener('click', (event) => {
        const link = event.target.closest('a[data-chat-link]');
        if (!link) return;
        trackChatEvent('chat_redirect', { chat_link: link.getAttribute('href') });
        this.persistTranscript();
        if (link.hasAttribute('data-chat-resumable')) this.persistResumeState();
      });

      window.addEventListener('pagehide', () => this.persistTranscript());
    }

    // Panel visibility
    async openPanel() {
      const { panel, launcher } = this.elements;
      panel.hidden = false;
      launcher.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      trackChatEvent('chat_open');

      if (!this.frames.length) await this.startConversation();
      this.focusFirstControl();
    }

    closePanel() {
      const { panel, launcher } = this.elements;
      panel.hidden = true;
      launcher.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      launcher.focus();
    }

    // Conversation lifecycle
    async startConversation() {
      this.frames = [];
      this.renderConversation();

      try {
        await this.showTyping(this.loadFlow());
      } catch (error) {
        console.error('[chat-bot] flow unavailable', error);
        this.flowPromise = null;
        this.renderUnavailableState();
        return;
      }

      const resumeState = readResumeState();
      if (resumeState && this.flow.settings.followUp) {
        this.pushFollowUpFrame(resumeState);
        return;
      }

      await this.runAction(() => this.openStep(this.flow.settings.startStep));
    }

    async restartConversation() {
      if (this.isBusy) return;
      this.previousTranscript = [];
      removeSessionItem(TRANSCRIPT_STORAGE_KEY);
      removeSessionItem(RESUME_STORAGE_KEY);
      await this.startConversation();
      this.focusFirstControl();
    }

    // Follow-up after returning from a product link
    persistResumeState() {
      const resumeIndex = this.frames.findLastIndex(({ view }) => RESUMABLE_VIEW_KINDS.has(view?.kind));
      if (resumeIndex < 0) return;

      writeSessionJson(RESUME_STORAGE_KEY, { savedAt: Date.now(), frames: this.frames.slice(0, resumeIndex + 1) });
    }

    pushFollowUpFrame(resumeState) {
      const { followUp } = this.flow.settings;
      this.pushFrame({
        stepId: null,
        choiceLabel: null,
        context: {},
        message: this.translate(followUp.message),
        view: { kind: 'follow-up', resumeState },
      });
    }

    renderFollowUpControls({ view }) {
      const { followUp } = this.flow.settings;
      const yesLabel = this.translate(followUp.yes);
      const noLabel = this.translate(followUp.no);

      return [
        this.createButtonGroup([
          this.createChoiceButton(yesLabel, () => this.answerFollowUp(true, yesLabel)),
          this.createChoiceButton(noLabel, () => this.resumeConversation(view.resumeState)),
        ]),
      ];
    }

    async answerFollowUp(isFound, choiceLabel) {
      removeSessionItem(RESUME_STORAGE_KEY);
      trackChatEvent('chat_follow_up', { part_found: isFound });
      await this.openStep(this.flow.settings.followUp.yesStep, { choiceLabel });
    }

    async resumeConversation(resumeState) {
      removeSessionItem(RESUME_STORAGE_KEY);
      trackChatEvent('chat_follow_up', { part_found: false });

      await this.showTyping(this.ensureVehicleIndex());
      this.previousTranscript = [];
      this.frames = resumeState.frames;
      this.renderConversation();
    }

    goBack() {
      if (this.isBusy || this.frames.length < 2) return;
      this.frames.pop();
      this.renderConversation();
      this.focusFirstControl();
    }

    async runAction(action) {
      if (this.isBusy) return;
      this.isBusy = true;

      try {
        await action();
      } catch (error) {
        console.error('[chat-bot]', error);
        this.pushErrorFrame();
      } finally {
        this.isBusy = false;
        this.focusFirstControl();
      }
    }

    // Flow loading and translations
    loadFlow() {
      this.flowPromise ??= fetchJson(this.config.flowUrl).then((flow) => {
        this.flow = flow;
        this.applyInterfaceTexts();
        return flow;
      });
      return this.flowPromise;
    }

    translate(text) {
      if (!text || typeof text === 'string') return text ?? '';

      const [language] = this.locale.split('-');
      return text[this.locale] ?? text[language] ?? text[this.flow?.defaultLocale] ?? Object.values(text)[0] ?? '';
    }

    translateUi(key) {
      return this.translate(this.flow?.settings?.ui?.[key]);
    }

    applyInterfaceTexts() {
      const { back, restart, close } = this.elements;
      back.textContent = this.translateUi('back');
      restart.textContent = this.translateUi('restart');
      close.setAttribute('aria-label', this.translateUi('close'));
    }

    buildTextVariables(context = {}) {
      return {
        vehicle: context.vehicleNodeId && this.vehicleIndex ? this.vehicleIndex.getPathLabel(context.vehicleNodeId) : '',
        category: context.categoryLabel ?? '',
        count: context.count ?? '',
      };
    }

    formatStepText(text, context) {
      return interpolateText(this.translate(text), this.buildTextVariables(context));
    }

    // URL helpers
    localizeUrl(url = '') {
      if (!url.startsWith('/') || url.startsWith('//') || !this.rootPrefix) return url;
      return url === this.rootPrefix || url.startsWith(`${this.rootPrefix}/`) ? url : `${this.rootPrefix}${url}`;
    }

    stripLocalePrefix(url = '') {
      return this.rootPrefix && url.startsWith(`${this.rootPrefix}/`) ? url.slice(this.rootPrefix.length) : url;
    }

    buildFormUrl(formKey, context) {
      const { forms } = this.flow.settings;
      const configuredUrl = forms?.[formKey]?.url ?? '';
      const baseUrl = !configuredUrl || configuredUrl.startsWith('TODO') ? forms.contact.url : configuredUrl;
      const url = new URL(this.localizeUrl(baseUrl), window.location.origin);

      const params = {
        source: 'chat',
        form: formKey,
        topic: context.topic,
        vehicle: this.buildTextVariables(context).vehicle,
        vehicle_id: context.vehicleNodeId,
        category: context.categoryLabel,
        category_tag: context.categoryTag,
        chat_path: this.frames.map(({ choiceLabel }) => choiceLabel).filter(Boolean).join(' > '),
        locale: this.locale,
      };
      Object.entries(params).forEach(([key, value]) => value && url.searchParams.set(key, value));

      return `${url.pathname}${url.search}`;
    }

    // Step routing
    async openStep(stepId, { choiceLabel = null, context = {} } = {}) {
      const { steps, settings } = this.flow;
      const resolvedStepId = steps[stepId] ? stepId : settings.fallbackStep;
      const step = steps[resolvedStepId];
      const frameBase = { stepId: resolvedStepId, choiceLabel, context };

      trackChatEvent('chat_step', { chat_step_id: resolvedStepId });

      const stepHandlers = {
        vehicle: () => this.startVehicleSelection(resolvedStepId, choiceLabel, context),
        category: () => this.showCategoryStep(resolvedStepId, choiceLabel, context),
        form: () =>
          this.pushFrame({
            ...frameBase,
            message: this.formatStepText(step.message, context),
            view: { kind: 'form', formKey: step.form },
          }),
        redirect: () =>
          this.pushFrame({
            ...frameBase,
            message: this.formatStepText(step.message, context),
            view: { kind: 'links', links: [{ label: step.linkLabel ?? step.message, url: step.url }] },
          }),
        default: () =>
          this.pushFrame({
            ...frameBase,
            message: this.formatStepText(step.message, context),
            view: { kind: 'options', options: this.getStepOptions(step), links: step.links ?? [] },
          }),
      };

      await (stepHandlers[step.type] ?? stepHandlers.default)();
    }

    resolveMaxButtons(step) {
      return step.maxButtons ?? this.flow.settings.maxButtons ?? 4;
    }

    getStepOptions(step) {
      return step.options ?? this.flow.optionSets?.[step.optionSet] ?? [];
    }

    selectOption(option, context) {
      trackChatEvent('chat_option', { chat_option_id: option.id });
      return this.openStep(option.target, {
        choiceLabel: this.translate(option.label),
        context: { ...context, ...option.context },
      });
    }

    pushFrame(frame) {
      this.frames.push(frame);
      this.renderConversation();
    }

    pushErrorFrame() {
      const fallbackStepId = this.flow?.settings?.fallbackStep;
      this.pushFrame({
        stepId: fallbackStepId,
        choiceLabel: null,
        context: this.frames.at(-1)?.context ?? {},
        message: this.translateUi('genericError') || this.config.unavailableMessage,
        view: { kind: 'options', options: [this.buildContactPageOption()], links: this.flow?.steps?.[fallbackStepId]?.links ?? [] },
      });
    }

    buildContactPageOption() {
      const { ui = {}, forms = {} } = this.flow?.settings ?? {};
      return {
        id: 'contact-page',
        label: ui.contactPage ?? this.config.fallbackLabel,
        url: forms.contact?.url ?? this.config.fallbackUrl,
      };
    }

    // Vehicle selection
    async ensureVehicleIndex() {
      if (this.vehicleIndex) return this.vehicleIndex;
      if (!window.MaxtonVehicleDataPromise) await loadScript(this.config.vehicleLoaderUrl);
      if (!window.MaxtonVehicleDataPromise) throw new Error('Vehicle taxonomy loader did not start');

      this.vehicleIndex = buildVehicleIndex(await window.MaxtonVehicleDataPromise);
      return this.vehicleIndex;
    }

    async startVehicleSelection(stepId, choiceLabel, context) {
      const vehicleIndex = await this.showTyping(this.ensureVehicleIndex());
      await this.pushVehicleLevel(stepId, vehicleIndex.rootId, choiceLabel, context);
    }

    resolveSingleChildChain(nodeId) {
      let currentId = Number(nodeId);
      let children = this.vehicleIndex.getChildren(currentId);
      let wasAutoSelected = false;

      while (children.length === 1) {
        currentId = Number(children[0].id);
        children = this.vehicleIndex.getChildren(currentId);
        wasAutoSelected = true;
      }

      return { nodeId: currentId, children, wasAutoSelected };
    }

    async pushVehicleLevel(stepId, nodeId, choiceLabel, context) {
      const { nodeId: resolvedNodeId, children, wasAutoSelected } = this.resolveSingleChildChain(nodeId);
      if (wasAutoSelected) await this.showTyping(wait(this.flow.settings.typingDelayMs ?? 700));

      const isRoot = resolvedNodeId === this.vehicleIndex.rootId;
      const vehicleContext = isRoot ? { ...context, vehicleNodeId: null } : { ...context, vehicleNodeId: resolvedNodeId };

      if (!children.length) {
        await this.completeVehicleSelection(stepId, choiceLabel, vehicleContext);
        return;
      }

      this.pushFrame({
        stepId,
        choiceLabel,
        context: vehicleContext,
        message: this.buildVehicleQuestion(stepId, resolvedNodeId, children),
        view: { kind: 'vehicle-level', nodeId: resolvedNodeId },
      });
    }

    async completeVehicleSelection(stepId, choiceLabel, context) {
      trackChatEvent('chat_vehicle_selected', { vehicle_id: context.vehicleNodeId });
      await this.openStep(this.flow.steps[stepId].onComplete, { choiceLabel, context });
    }

    buildVehicleQuestion(stepId, nodeId, children) {
      const { messages = {} } = this.flow.steps[stepId];
      const template = messages[children[0].type] ?? messages.model;
      return interpolateText(this.translate(template), { parent: this.vehicleIndex.getNode(nodeId)?.label ?? '' });
    }

    pickPopularNodes(children, popularIds = [], limit) {
      const childrenById = new Map(children.map((child) => [Number(child.id), child]));
      const popularChildren = popularIds.map((id) => childrenById.get(Number(id))).filter(Boolean);
      const remainingChildren = children.filter((child) => !popularChildren.includes(child));
      return [...popularChildren, ...remainingChildren].slice(0, limit);
    }

    // Controls rendering
    renderFrameControls(frame) {
      if (!frame) return [];

      const renderers = {
        options: () => this.renderOptionControls(frame),
        links: () => [this.renderLinkList(frame.view.links, { asButtons: true })],
        form: () => this.renderFormControls(frame),
        'vehicle-level': () => this.renderVehicleLevelControls(frame),
        'vehicle-picker': () => this.renderVehiclePickerControls(frame),
        categories: () => this.renderCategoryControls(frame),
        'category-missing': () => this.renderMissingCategoryControls(frame),
        products: () => this.renderProductControls(frame),
        'follow-up': () => this.renderFollowUpControls(frame),
      };

      return (renderers[frame.view.kind]?.() ?? []).filter(Boolean);
    }

    renderOptionControls({ view, context }) {
      const buttons = view.options.map((option) =>
        option.url
          ? this.createLinkButton(this.translate(option.label), this.translate(option.url))
          : this.createChoiceButton(this.translate(option.label), () => this.selectOption(option, context)),
      );

      return [this.renderLinkList(view.links), this.createButtonGroup(buttons)];
    }

    renderFormControls({ view, context }) {
      const formUrl = this.buildFormUrl(view.formKey, context);
      return [this.createButtonGroup([this.createLinkButton(this.translateUi('openForm'), formUrl, { isPrimary: true })])];
    }

    renderVehicleLevelControls(frame) {
      const step = this.flow.steps[frame.stepId];
      const labels = step.labels ?? {};
      const { nodeId } = frame.view;
      const isRoot = nodeId === this.vehicleIndex.rootId;
      const children = this.vehicleIndex.getChildren(nodeId);
      const maxButtons = this.resolveMaxButtons(step);

      const popularIds = isRoot ? step.popularBrands : step.popularModels?.[nodeId];
      const visibleLimit = children.length <= maxButtons ? children.length : maxButtons - 1;
      const visibleChildren = this.pickPopularNodes(children, popularIds, visibleLimit);
      const choiceButtons = visibleChildren.map((child) =>
        this.createChoiceButton(child.label, () => this.pushVehicleLevel(frame.stepId, child.id, child.label, frame.context)),
      );

      if (visibleChildren.length < children.length) {
        const pickerLabel = isRoot ? this.translate(labels.otherBrand) : `${this.translate(labels.showAll)} (${children.length})`;
        choiceButtons.push(
          this.createChoiceButton(pickerLabel, () =>
            this.pushFrame({ ...frame, choiceLabel: pickerLabel, message: null, view: { kind: 'vehicle-picker', nodeId } }),
          ),
        );
      }

      return [
        this.createButtonGroup(choiceButtons),
        isRoot && this.createNodeFinder(frame, { searchAllLevels: true, showInitialResults: false }),
        this.createButtonGroup(this.buildVehicleSecondaryButtons(frame, isRoot), { isSecondary: true }),
      ];
    }

    renderVehiclePickerControls(frame) {
      const isRoot = frame.view.nodeId === this.vehicleIndex.rootId;
      return [
        this.createNodeFinder(frame, { searchAllLevels: isRoot, showInitialResults: true }),
        this.createButtonGroup(this.buildVehicleSecondaryButtons(frame, isRoot), { isSecondary: true }),
      ];
    }

    buildVehicleSecondaryButtons(frame, isRoot) {
      const step = this.flow.steps[frame.stepId];
      const labels = step.labels ?? {};
      const currentLabel = this.vehicleIndex.getNode(frame.view.nodeId)?.label ?? '';
      const showAllLabel = interpolateText(this.translate(labels.showAllForCurrent), { current: currentLabel });
      const missingVehicleLabel = this.translate(labels.missingVehicle);

      return [
        !isRoot &&
          this.createChoiceButton(showAllLabel, () => this.completeVehicleSelection(frame.stepId, showAllLabel, frame.context)),
        this.createChoiceButton(missingVehicleLabel, () =>
          this.openStep(step.onMissingVehicle, {
            choiceLabel: missingVehicleLabel,
            context: { ...frame.context, topic: 'missing-vehicle' },
          }),
        ),
      ].filter(Boolean);
    }

    createNodeFinder(frame, { searchAllLevels, showInitialResults }) {
      const step = this.flow.steps[frame.stepId];
      const placeholder = this.translate(step.labels?.searchPlaceholder);
      const children = this.vehicleIndex.getChildren(frame.view.nodeId);

      const findEntries = (query) => {
        if (searchAllLevels && query.trim()) return this.vehicleIndex.searchNodes(query);

        const normalizedQuery = normalizeText(query);
        return children
          .filter((child) => normalizeText(child.label).includes(normalizedQuery))
          .map((child) => ({ nodeId: Number(child.id), label: child.label }));
      };

      const input = createElement('input', {
        className: 'chat-bot__search',
        attributes: { type: 'search', placeholder, 'aria-label': placeholder, autocomplete: 'off' },
      });
      const results = createElement('div', { className: 'chat-bot__results' });

      const renderResults = () => {
        const hasQuery = input.value.trim().length > 0;
        const entries = hasQuery || showInitialResults ? findEntries(input.value) : [];

        results.replaceChildren(
          ...entries.map((entry) =>
            this.createChoiceButton(
              entry.label,
              () => this.pushVehicleLevel(frame.stepId, entry.nodeId, entry.label, frame.context),
              { isListItem: true },
            ),
          ),
        );
        if (hasQuery && !entries.length) {
          results.append(createElement('p', { className: 'chat-bot__hint', text: this.translateUi('searchNoResults') }));
        }
      };

      input.addEventListener('input', renderResults);
      renderResults();

      return createElement('div', { className: 'chat-bot__finder' }, [input, results]);
    }

    // Category step
    async fetchCollectionCatalog(node) {
      const collectionPaths = [...new Set([node.link, this.stripLocalePrefix(node.link)])];
      let lastError = null;

      for (const collectionPath of collectionPaths) {
        try {
          return await fetchAllCatalogPages(collectionPath);
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError;
    }

    buildCategoryCollectionUrl(step, node, category) {
      const template = step.filterUrlTemplate ?? '{collection}?filter.p.tag={value}';
      return interpolateText(template, { collection: node.link, value: encodeURIComponent(category.tag) });
    }

    listCategoriesWithCounts(step, catalog) {
      return step.categories.map((category) => ({
        ...category,
        label: this.translate(category.label),
        count: catalog?.countsByTag.get(normalizeText(category.tag)) ?? 0,
      }));
    }

    async showCategoryStep(stepId, choiceLabel, context) {
      const step = this.flow.steps[stepId];
      const node = this.vehicleIndex?.getNode(context.vehicleNodeId);

      if (!node) {
        await this.openStep(this.flow.settings.fallbackStep, { choiceLabel, context });
        return;
      }

      const catalog = await this.showTyping(
        this.fetchCollectionCatalog(node).catch((error) => {
          console.warn('[chat-bot] catalog unavailable', error);
          return null;
        }),
      );
      const frameBase = { stepId, choiceLabel, context };

      if (catalog?.total === 0) {
        this.pushFrame({
          ...frameBase,
          message: this.formatStepText(step.noProductsMessage, context),
          view: { kind: 'category-missing', categories: this.listCategoriesWithCounts(step, null) },
        });
        return;
      }

      const directResultLimit = step.directResultMaxProducts ?? 0;
      if (catalog && catalog.total <= directResultLimit) {
        this.pushProductResult({ ...frameBase, nodeId: node.id, catalog, allowMissingPart: true });
        return;
      }

      this.pushFrame({
        ...frameBase,
        message: this.formatStepText(step.message, context),
        view: { kind: 'categories', nodeId: node.id, catalog },
      });
    }

    createMissingPartButton(frame, catalog) {
      const step = this.flow.steps[frame.stepId];
      const missingPartLabel = this.translate(step.labels?.missingPart);
      const hasNarrowerVehicles = this.vehicleIndex.getChildren(frame.view.nodeId).length > 0;

      if (hasNarrowerVehicles && step.vehicleStep) {
        return this.createChoiceButton(missingPartLabel, () => this.askToNarrowVehicle(frame, missingPartLabel));
      }

      const missingCategories = this.listCategoriesWithCounts(step, catalog).filter(({ count }) => !count);

      return this.createChoiceButton(missingPartLabel, () =>
        this.pushFrame({
          ...frame,
          choiceLabel: missingPartLabel,
          message: null,
          view: { kind: 'category-missing', categories: missingCategories },
        }),
      );
    }

    askToNarrowVehicle(frame, choiceLabel) {
      const step = this.flow.steps[frame.stepId];
      const children = this.vehicleIndex.getChildren(frame.view.nodeId);
      this.pushFrame({
        stepId: step.vehicleStep,
        choiceLabel,
        context: frame.context,
        message: [this.formatStepText(step.narrowVehicleMessage, frame.context), this.buildVehicleQuestion(step.vehicleStep, frame.view.nodeId, children)].join(' '),
        view: { kind: 'vehicle-level', nodeId: frame.view.nodeId },
      });
    }

    renderCategoryControls(frame) {
      const step = this.flow.steps[frame.stepId];
      const labels = step.labels ?? {};
      const { catalog, nodeId } = frame.view;
      const node = this.vehicleIndex.getNode(nodeId);
      const categories = this.listCategoriesWithCounts(step, catalog);
      const maxButtons = this.resolveMaxButtons(step);
      const missingPartButton = this.createMissingPartButton(frame, catalog);

      if (!catalog) {
        return [
          this.createButtonGroup([this.createLinkButton(this.translate(labels.showProducts), node.link, { isPrimary: true, isResumable: true })]),
          this.createButtonGroup([missingPartButton], { isSecondary: true }),
        ];
      }

      const availableCategories = categories.filter(({ count }) => count > 0).slice(0, maxButtons - 1);
      const allLabel = this.translate(labels.all);
      const choiceButtons = [
        ...availableCategories.map((category) =>
          this.createChoiceButton(`${category.label} (${formatCatalogCount(category.count, catalog)})`, () =>
            this.showCategoryResult(frame, category, category.label),
          ),
        ),
        this.createChoiceButton(`${allLabel} (${formatCatalogCount(catalog.total, catalog)})`, () => this.showCategoryResult(frame, null, allLabel)),
      ];

      return [this.createButtonGroup(choiceButtons), this.createButtonGroup([missingPartButton], { isSecondary: true })];
    }

    showCategoryResult(frame, category, choiceLabel) {
      const { stepId, context, view } = frame;
      this.pushProductResult({ stepId, choiceLabel, context, nodeId: view.nodeId, catalog: view.catalog, category });
    }

    pushProductResult({ stepId, choiceLabel, context, nodeId, catalog, category = null, allowMissingPart = false }) {
      const step = this.flow.steps[stepId];
      const node = this.vehicleIndex.getNode(nodeId);
      const products = category ? filterProductsByTag(catalog.products, category.tag) : catalog.products;
      const resultContext = { ...context, categoryLabel: category?.label ?? null, count: formatCatalogCount(products.length, catalog) };
      const collectionUrl = category ? this.buildCategoryCollectionUrl(step, node, category) : node.link;

      trackChatEvent('chat_category', {
        chat_category_id: category?.id ?? (allowMissingPart ? 'direct' : 'all'),
        product_count: products.length,
      });

      this.pushFrame({
        stepId,
        choiceLabel,
        context: resultContext,
        message: this.formatStepText(step.resultMessage, resultContext),
        view: {
          kind: 'products',
          nodeId,
          catalog,
          products: products.slice(0, PRODUCT_CARD_LIMIT),
          count: formatCatalogCount(products.length, catalog),
          collectionUrl,
          allowMissingPart,
        },
      });
    }

    renderMissingCategoryControls(frame) {
      const step = this.flow.steps[frame.stepId];
      const otherPart = { label: this.translate(step.labels?.otherPart), tag: null };
      const missingParts = [...frame.view.categories, otherPart];

      const buttons = missingParts.map(({ label, tag }) =>
        this.createChoiceButton(label, () =>
          this.openStep(step.onMissingPart, {
            choiceLabel: label,
            context: { ...frame.context, categoryLabel: label, categoryTag: tag, topic: 'missing-part' },
          }),
        ),
      );

      return [this.createButtonGroup(buttons)];
    }

    renderProductControls(frame) {
      const step = this.flow.steps[frame.stepId];
      const { products, count, collectionUrl, catalog, allowMissingPart } = frame.view;
      const showProductsLabel = `${this.translate(step.labels?.showProducts)} (${count})`;

      return [
        products.length ? createElement('div', { className: 'chat-bot__products' }, products.map((product) => this.createProductCard(product))) : null,
        this.createButtonGroup([this.createLinkButton(showProductsLabel, collectionUrl, { isPrimary: true, isResumable: true })]),
        allowMissingPart && this.createButtonGroup([this.createMissingPartButton(frame, catalog)], { isSecondary: true }),
      ];
    }

    // Product cards
    createProductCard(product) {
      const imageUrl = buildResizedImageUrl(product.images?.[0]?.src, PRODUCT_IMAGE_WIDTH);
      const title = createElement('span', { className: 'chat-bot__product-title', text: product.title });
      const price = createElement('span', { className: 'chat-bot__product-price' });

      const card = createElement(
        'a',
        {
          className: 'chat-bot__product',
          attributes: {
            href: this.localizeUrl(`/products/${product.handle}`),
            title: product.title,
            'data-chat-link': true,
            'data-chat-resumable': true,
          },
        },
        [
          createElement('span', { className: 'chat-bot__product-thumb' }, [
            imageUrl && createElement('img', { attributes: { src: imageUrl, alt: '', loading: 'lazy', width: PRODUCT_THUMB_SIZE, height: PRODUCT_THUMB_SIZE } }),
          ]),
          createElement('span', { className: 'chat-bot__product-info' }, [title, price]),
          createChevronIcon(),
        ],
      );

      this.showCatalogPrice(product, price);
      this.hydrateProductCard(product.handle, { card, title, price });
      return card;
    }

    showCatalogPrice(product, price) {
      const catalogPrice = readLowestVariantPriceInCents(product);
      const isBaseCurrencyActive = this.activeCurrency === this.config.shopCurrency;
      if (catalogPrice !== null && isBaseCurrencyActive) price.textContent = this.formatMoney(catalogPrice);
    }

    async hydrateProductCard(handle, { card, title, price }) {
      try {
        const product = await fetchJson(this.localizeUrl(`/products/${handle}.js`));
        title.textContent = product.title;
        card.title = product.title;
        price.textContent = this.formatMoney(product.price_min ?? product.price);
      } catch (error) {
        console.warn('[chat-bot] product price unavailable', handle, error);
        if (!price.textContent) price.remove();
      }
    }

    get activeCurrency() {
      return window.Shopify?.currency?.active ?? this.config.currency;
    }

    formatMoney(amountInCents) {
      return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.activeCurrency }).format(amountInCents / 100);
    }

    // UI primitives
    createBubble(role, text, isHistory = false) {
      const className = ['chat-bot__bubble', `chat-bot__bubble--${role}`, isHistory && 'chat-bot__bubble--history']
        .filter(Boolean)
        .join(' ');
      return createElement('p', { className, text });
    }

    createChoiceButton(label, onSelect, { isListItem = false } = {}) {
      const className = ['chat-bot__choice', isListItem && 'chat-bot__choice--list'].filter(Boolean).join(' ');
      const button = createElement('button', { className, text: label, attributes: { type: 'button' } });
      button.addEventListener('click', () => this.runAction(onSelect));
      return button;
    }

    createLinkButton(label, url, { isPrimary = false, isResumable = false } = {}) {
      const className = ['chat-bot__choice', isPrimary && 'chat-bot__choice--primary'].filter(Boolean).join(' ');
      return createElement('a', {
        className,
        text: label,
        attributes: { href: this.localizeUrl(url), 'data-chat-link': true, 'data-chat-resumable': isResumable },
      });
    }

    createButtonGroup(buttons, { isSecondary = false } = {}) {
      if (!buttons.length) return null;
      const className = ['chat-bot__choices', isSecondary && 'chat-bot__choices--secondary'].filter(Boolean).join(' ');
      return createElement('div', { className }, buttons);
    }

    renderLinkList(links = [], { asButtons = false } = {}) {
      if (!links.length) return null;
      const items = links.map(({ label, url }) =>
        asButtons
          ? this.createLinkButton(this.translate(label), this.translate(url), { isPrimary: true })
          : createElement('a', { className: 'chat-bot__link', text: this.translate(label), attributes: { href: this.localizeUrl(this.translate(url)), 'data-chat-link': true } }),
      );
      return createElement('div', { className: asButtons ? 'chat-bot__choices' : 'chat-bot__links' }, items);
    }

    // Conversation rendering
    renderConversation() {
      const { messages, controls, back } = this.elements;
      const historyLabel = this.translateUi('previousConversation');

      const historyBubbles = this.previousTranscript.length
        ? [
            historyLabel && createElement('p', { className: 'chat-bot__separator', text: historyLabel }),
            ...this.previousTranscript.map(({ role, text }) => this.createBubble(role, text, true)),
          ]
        : [];

      const frameBubbles = this.frames.flatMap(({ choiceLabel, message }) => [
        choiceLabel && this.createBubble('user', choiceLabel),
        message && this.createBubble('bot', message),
      ]);

      messages.replaceChildren(...[...historyBubbles, ...frameBubbles].filter(Boolean));
      controls.replaceChildren(...this.renderFrameControls(this.frames.at(-1)));
      back.disabled = this.frames.length < 2;
      this.scrollMessagesToEnd();
    }

    renderUnavailableState() {
      const { messages, controls } = this.elements;
      const { unavailableMessage, fallbackUrl, fallbackLabel } = this.config;

      messages.replaceChildren(this.createBubble('bot', unavailableMessage));
      controls.replaceChildren(
        createElement('div', { className: 'chat-bot__choices' }, [
          createElement('a', { className: 'chat-bot__choice chat-bot__choice--primary', text: fallbackLabel, attributes: { href: this.localizeUrl(fallbackUrl) } }),
        ]),
      );
    }

    async showTyping(task) {
      const { messages, controls } = this.elements;
      const indicator = createElement(
        'div',
        { className: 'chat-bot__bubble chat-bot__bubble--bot chat-bot__typing', attributes: { role: 'status', 'aria-label': this.translateUi('typing') } },
        [createElement('span'), createElement('span'), createElement('span')],
      );

      controls.replaceChildren();
      messages.append(indicator);
      this.scrollMessagesToEnd();

      try {
        return await task;
      } finally {
        indicator.remove();
      }
    }

    scrollMessagesToEnd() {
      const { messages } = this.elements;
      messages.scrollTop = messages.scrollHeight;
    }

    focusFirstControl() {
      if (this.elements.panel.hidden) return;
      this.elements.controls.querySelector('button:not([disabled]), a[href], input')?.focus({ preventScroll: true });
    }

    // Transcript persistence
    persistTranscript() {
      if (this.frames.length < 2) return;

      const currentTranscript = this.frames.flatMap(({ choiceLabel, message }) =>
        [choiceLabel && { role: 'user', text: choiceLabel }, message && { role: 'bot', text: message }].filter(Boolean),
      );
      writeSessionJson(TRANSCRIPT_STORAGE_KEY, [...this.previousTranscript, ...currentTranscript].slice(-TRANSCRIPT_LIMIT));
    }
  }

  customElements.define('maxton-chat-bot', MaxtonChatBot);
})();